import type { AppNotification, NotificationType } from "@/types/notification"
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react"
import { restoreFavicon, setFaviconWithBadge } from "@/lib/favicon-badge"
import {
  useInjectNotificationHistoryItem,
  useNotificationHistories,
  useNotificationReadActions,
} from "@/hooks/use-notification-histories"

import type { UsePushNotificationsOptions } from "@/hooks/use-push-notifications"
import { useAppKitAccount } from "@reown/appkit/react"
import { useAuth } from "@/contexts/auth-context"
import { useConnection } from "wagmi"
import { useIntl } from "react-intl"
import { usePushNotifications } from "@/hooks/use-push-notifications"

interface NotificationContextValue {
  notifications: AppNotification[]
  unreadCount: number
  latest: AppNotification | undefined
  isLoading: boolean
  isFetchingNextPage: boolean
  isRefetchingNotifications: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
  addNotification: (payload: {
    type?: NotificationType | string
    title: string
    description?: string
    data?: Record<string, unknown>
    rawPayload?: string
  }) => void
  refetchNotifications: () => void
  markRead: (id: string) => void
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

const NOTIFICATION_CHANNEL_NAME = "avacus-notification-channel"

/**
 * Listens to BroadcastChannel (SW sends when a noti arrives in background).
 * Adds to context in real-time; if tab is hidden, blinks document.title between
 * "New notification" and the original title every 2s (like Messenger). Restores on focus.
 * When this tab gets focus, broadcasts "stopTitleBlink" so other tabs stop blinking too.
 */
function NotificationBroadcastAndTitle({
  addNotification,
  userAddress,
}: {
  addNotification: (payload: {
    type?: NotificationType | string
    title: string
    description?: string
    data?: Record<string, unknown>
    rawPayload?: string
  }) => void
  userAddress?: string | null
}) {
  const intl = useIntl()
  const savedTitleRef = useRef("")
  const blinkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const newNotificationTitle = intl.formatMessage({
    id: "layout.newNotificationTitle",
  })

  useEffect(() => {
    function stopBlinking() {
      if (blinkIntervalRef.current) {
        clearInterval(blinkIntervalRef.current)
        blinkIntervalRef.current = null
      }
    }

    function forceStopBlinkingAndRestore() {
      stopBlinking()
      if (savedTitleRef.current) {
        document.title = savedTitleRef.current
        savedTitleRef.current = ""
      }
      restoreFavicon()
    }

    function restoreTitle() {
      if (document.visibilityState === "visible") {
        forceStopBlinkingAndRestore()
      }
    }

    function startBlinking(originalTitle: string) {
      stopBlinking()
      let showNew = true
      document.title = newNotificationTitle
      blinkIntervalRef.current = setInterval(() => {
        if (document.hidden) {
          document.title = showNew ? newNotificationTitle : originalTitle
          showNew = !showNew
        }
      }, 2000)
    }

    function onChannelMessage(ev: MessageEvent) {
      const msg = ev.data
      if (!msg || !msg.type) return
      if (msg.type === "stopTitleBlink") {
        forceStopBlinkingAndRestore()
        return
      }
      if (msg.type !== "notification" || !msg.data) return

      const notiWallet = (msg.data.data ?? msg.data)?.wallet_address
      if (notiWallet != null && typeof notiWallet === "string") {
        const notiAddr = notiWallet.toLowerCase()
        const userAddr = (userAddress ?? "").toLowerCase()
        if (notiAddr !== userAddr) return
      }

      addNotification({
        type: msg.data.type,
        title: msg.data.title ?? "Notification",
        description: msg.data.description,
        data: msg.data.data ?? msg.data,
        rawPayload: msg.data.rawPayload,
      })
      if (document.hidden) {
        if (!savedTitleRef.current) savedTitleRef.current = document.title
        startBlinking(savedTitleRef.current)
        setFaviconWithBadge()
      }
    }

    restoreTitle()
    const channel =
      typeof BroadcastChannel !== "undefined"
        ? new BroadcastChannel(NOTIFICATION_CHANNEL_NAME)
        : null
    channel?.addEventListener("message", onChannelMessage)

    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        restoreTitle()
        channel?.postMessage({ type: "stopTitleBlink" })
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange)

    return () => {
      forceStopBlinkingAndRestore()
      channel?.removeEventListener("message", onChannelMessage)
      channel?.close()
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [addNotification, userAddress, newNotificationTitle])

  return null
}

function PushNotificationsSetup({
  isReady,
  refetchProfile,
}: UsePushNotificationsOptions) {
  usePushNotifications({ isReady, refetchProfile })
  return null
}

export function NotificationProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const { address, isVerified, isFetchingUser, refetchProfile } = useAuth()
  const { address: connectedAddress } = useConnection()
  const { address: appKitAddress } = useAppKitAccount()
  const notificationScope = connectedAddress ?? appKitAddress ?? address ?? null
  const canFetchNotificationHistories = Boolean(
    notificationScope && isVerified && !isFetchingUser
  )
  const {
    notifications,
    unreadCount,
    latest,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isRefetching,
    refetch,
  } = useNotificationHistories(notificationScope, canFetchNotificationHistories)
  const { markRead } = useNotificationReadActions()
  const injectNotificationItem = useInjectNotificationHistoryItem()

  const addNotification = useCallback(
    (payload: {
      type?: NotificationType | string
      title: string
      description?: string
      data?: Record<string, unknown>
      rawPayload?: string
    }) => {
      const notiWallet = payload.data?.wallet_address
      if (notiWallet != null && typeof notiWallet === "string") {
        const notiAddr = notiWallet.toLowerCase()
        const userAddr = (notificationScope ?? "").toLowerCase()
        if (notiAddr !== userAddr) return
      }

      injectNotificationItem({ payload, scope: notificationScope })
    },
    [notificationScope, injectNotificationItem]
  )

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      latest,
      isLoading,
      isRefetchingNotifications: isRefetching,
      isFetchingNextPage,
      hasNextPage: Boolean(hasNextPage),
      fetchNextPage: () => {
        void fetchNextPage()
      },
      addNotification,
      refetchNotifications: () => {
        void refetch()
      },
      markRead: (id: string) => {
        markRead(id, notificationScope)
      },
    }),
    [
      notifications,
      unreadCount,
      latest,
      isLoading,
      isRefetching,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
      addNotification,
      refetch,
      markRead,
      notificationScope,
    ]
  )

  return (
    <NotificationContext.Provider value={value}>
      <NotificationBroadcastAndTitle
        addNotification={addNotification}
        userAddress={notificationScope}
      />
      <PushNotificationsSetup
        isReady={isVerified && !isFetchingUser}
        refetchProfile={refetchProfile}
      />
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotificationContext() {
  const ctx = useContext(NotificationContext)
  if (!ctx)
    throw new Error(
      "useNotificationContext must be used within NotificationProvider"
    )
  return ctx
}
